import { createSelector } from 'reselect';

import { dataKiSyokenFilteredByKiTagList } from '../../domain/kiSyoken/dataKiSyokenFilteredByKTagiList';
import { dataKiSyokenFilteredByKiList } from '../../domain/kiSyoken/dataKiSyokenFilteredByKiList';
import { dataKiSyokenHiddenByMksHall } from '../../domain/kiSyoken/dataKiSyokenFilteredByMksHall';
import { dataKiSyokenFilteredByName } from '../../domain/kiSyoken/dataKiSyokenFilteredByName';
import { dataKiSyokenFilteredByNumber } from '../../domain/kiSyoken/dataKiSyokenFilteredByNumber';
import { dataKiSyokenHiddenByField } from '../../domain/kiSyoken/dataKiSyokenHiddenByField';
import { dataKiSyokenSwapColumnsOrder } from '../../domain/kiSyoken/dataKiSyokenSwapColumnsOrder';
import { removeKyakuSuKiSyoken } from '../../domain/kiSyoken/removeKyakusuKiSyoken';
import { sortKiSyoken } from '../../domain/kiSyoken/sortKiSyoken';
import { DataKiSyokenResponse } from '../../domain/kiSyoken/types';
import { ColumnForDataTable } from '../../domain/schemas';

import {
  columnIndexByCode,
  filterRowsKeepingLeadingAverage,
  normalizePpmKbnValue,
  stringCellValue,
} from '../../utils/tableHeaderSubMenuMultiFilter';
import {
  kiSyokenColumnsOrderSelector,
  kiSyokenListFilterSelector,
  kiSyokenMksHallCodesSelector,
  kiSyokenNameFilterSelector,
  kiSyokenOrderSelector,
  kiSyokenSelectedTableFilterSelector,
  kiSyokenSortSelector,
  kiSyokenSubMenuNumberFilterSelector,
  kiSyokenTableHeaderSubMenuMultiFilterSelector,
  kiSyokenTagListSelector,
} from '../ui/kiSyokenSettings';
import {
  dataKiSyokenDataSelector,
  dataKiSyokenKiFilteredTagsAverageSelector,
  dataKiSyokenKiListAverageDataSelector,
  dataKiSyokenKiTagsAverageSelector,
} from './dataKiSyoken';

type KiSyokenTableHeaderSubMenuMultiFilter = {
  [columnName: string]: string[];
};

const PPM_MULTI_FILTER_COLUMN = 'ppmKbn';

export const applyKiSyokenMultiFiltering = (
  filteredRows: DataKiSyokenResponse['data']['rows'],
  tableHeaderSubMenuMultiFilter:
    | KiSyokenTableHeaderSubMenuMultiFilter
    | undefined,
  columns: readonly ColumnForDataTable[],
  containsAverage: boolean
) => {
  const columnIndexMap = columnIndexByCode(columns);

  return filterRowsKeepingLeadingAverage(
    filteredRows,
    containsAverage,
    tableHeaderSubMenuMultiFilter,
    (row, columnCode) => {
      const rawCellValue = stringCellValue(columnIndexMap, row, columnCode);
      if (rawCellValue == null) {
        return rawCellValue;
      }
      return columnCode === PPM_MULTI_FILTER_COLUMN
        ? normalizePpmKbnValue(rawCellValue)
        : rawCellValue;
    }
  );
};

/**
 * [機種別商圏] dataのrowsから不要な単位を削除したデータ
 *
 */
const dataKiSyokenRemovedSelector = createSelector(
  [dataKiSyokenDataSelector],
  (dataKiSyokenData) => {
    if (dataKiSyokenData == null) {
      return dataKiSyokenData;
    }

    // 人の単位を削除
    const removedDataKiSyoken = removeKyakuSuKiSyoken(dataKiSyokenData);

    return removedDataKiSyoken;
  }
);

/**
 * [機種別商圏] KiFilteredTagsAverageのrowsから不要な単位を削除したデータ
 */
const dataKiSyokenKiFilteredTagsAverageRemovedSelector = createSelector(
  [dataKiSyokenKiFilteredTagsAverageSelector],
  (dataKiSyokenKiFilteredTagsAverage) => {
    if (dataKiSyokenKiFilteredTagsAverage == null) {
      return dataKiSyokenKiFilteredTagsAverage;
    }

    // 人の単位を削除
    const removedDataKiSyokenKiFilteredTagsAverage = removeKyakuSuKiSyoken(
      dataKiSyokenKiFilteredTagsAverage
    );

    return removedDataKiSyokenKiFilteredTagsAverage;
  }
);

/**
 * [機種別商圏] KiTagsAverageのrowsから不要な単位を削除したデータ
 */
const dataKiSyokenKiTagsAverageRemovedSelector = createSelector(
  [dataKiSyokenKiTagsAverageSelector],
  (dataKiSyokenKiTagsAverage) => {
    if (dataKiSyokenKiTagsAverage == null) {
      return dataKiSyokenKiTagsAverage;
    }

    // 配列の各要素に対して単位削除処理を適用
    return dataKiSyokenKiTagsAverage.map((dataItem) => {
      return removeKyakuSuKiSyoken(dataItem);
    });
  }
);

/**
 * [機種別商圏] ソート処理によって整形されたデータ (ソートラベル・サブメニュー時)
 *
 */
const dataKiSyokenSortedSelector = createSelector(
  [dataKiSyokenRemovedSelector, kiSyokenSortSelector, kiSyokenOrderSelector],
  (dataKiSyokenData, kiSyokenSort, kiSyokenOrder) => {
    if (
      kiSyokenSort == null ||
      kiSyokenOrder == null ||
      dataKiSyokenData == null
    ) {
      return dataKiSyokenData;
    }

    const sortedDataKiSyoken = sortKiSyoken(
      dataKiSyokenData,
      kiSyokenOrder,
      kiSyokenSort
    );
    return sortedDataKiSyoken;
  }
);

/**
 * [機種別商圏] KiListAverageのrowsから不要な単位を削除したデータ
 */
const dataKiSyokenKiListAverageRemovedSelector = createSelector(
  [dataKiSyokenKiListAverageDataSelector],
  (dataKiSyokenKiListAverageData) => {
    if (dataKiSyokenKiListAverageData == null) {
      return dataKiSyokenKiListAverageData;
    }

    // 人の単位を削除
    const removedDataKiSyokenKiListAverage = removeKyakuSuKiSyoken(
      dataKiSyokenKiListAverageData
    );

    return removedDataKiSyokenKiListAverage;
  }
);

/**
 * [機種別商圏] KiListAverageのソート処理によって整形されたデータ (ソートラベル・サブメニュー時)
 *
 */
const dataKiSyokenKiListAverageSortedSelector = createSelector(
  [
    dataKiSyokenKiListAverageRemovedSelector,
    kiSyokenSortSelector,
    kiSyokenOrderSelector,
  ],
  (removedDataKiSyokenKiListAverage, kiSyokenSort, kiSyokenOrder) => {
    if (
      kiSyokenSort == null ||
      kiSyokenOrder == null ||
      removedDataKiSyokenKiListAverage == null
    ) {
      return removedDataKiSyokenKiListAverage;
    }

    const sortedDataKiSyokenKiListAverage = sortKiSyoken(
      removedDataKiSyokenKiListAverage,
      kiSyokenOrder,
      kiSyokenSort
    );
    return sortedDataKiSyokenKiListAverage;
  }
);

/**
 * [機種別商圏] フィルターによって行がフィルタリングされたデータ
 */
const dataRowsFilteredSelector = createSelector(
  [
    dataKiSyokenSortedSelector,
    kiSyokenNameFilterSelector,
    kiSyokenSubMenuNumberFilterSelector,
    kiSyokenTagListSelector,
    kiSyokenListFilterSelector,
    kiSyokenTableHeaderSubMenuMultiFilterSelector,
  ],
  (
    dataKiSyoken,
    nameFilter,
    numberFilter,
    kiTagList,
    kiListFilter,
    tableHeaderSubMenuMultiFilter
  ) => {
    if (dataKiSyoken == null) {
      return dataKiSyoken;
    }

    const filteredKiTagList = dataKiSyokenFilteredByKiTagList(
      dataKiSyoken,
      kiTagList
    );

    const filteredKiTagListAndKiList = dataKiSyokenFilteredByKiList(
      filteredKiTagList,
      kiListFilter
    );

    const filteredByNameAndKiListAndKiTagListAndKiList =
      dataKiSyokenFilteredByName(filteredKiTagListAndKiList, nameFilter);

    const filteredByNameAndNumberAndKiListAndKiTagListAndKiList =
      dataKiSyokenFilteredByNumber(
        filteredByNameAndKiListAndKiTagListAndKiList,
        numberFilter
      );

    const { columns, rows } =
      filteredByNameAndNumberAndKiListAndKiTagListAndKiList.data;

    const multiFilteredRows = applyKiSyokenMultiFiltering(
      rows,
      tableHeaderSubMenuMultiFilter,
      columns,
      filteredByNameAndNumberAndKiListAndKiTagListAndKiList.setting
        .containsAverage
    );

    return {
      ...filteredByNameAndNumberAndKiListAndKiTagListAndKiList,
      data: {
        ...filteredByNameAndNumberAndKiListAndKiTagListAndKiList.data,
        rows: multiFilteredRows,
      },
    };
  }
);

/**
 * [機種別商圏] 通常は使用しません dataKiSyokenResultSelector  を使用してください
 *
 * 表示項目の新規項目追加時に、columnsOrderの更新が必要になりますが
 * 一度もDnDをしていない場合にはcolumnsOrderが存在していないため実テーブルのデータを使用します
 * そのため通常は使用しません。最終的に表示させたいデータを取得したい場合にはdataKiSyokenResultSelectorを使用してください
 */
export const dataColumnsSwappedSelector = createSelector(
  [dataRowsFilteredSelector, kiSyokenColumnsOrderSelector],
  (filteredDataKiSyoken, columnsOrder) => {
    if (filteredDataKiSyoken == null) {
      return filteredDataKiSyoken;
    }

    const swappedFieldsOrder = dataKiSyokenSwapColumnsOrder(
      filteredDataKiSyoken,
      columnsOrder
    );

    return swappedFieldsOrder;
  }
);

/**
 * [機種別商圏] KiListAverageのカラムスワップ処理
 *
 * 表示項目の新規項目追加時に、columnsOrderの更新が必要になりますが
 * 一度もDnDをしていない場合にはcolumnsOrderが存在していないため実テーブルのデータを使用します
 */
export const dataKiSyokenKiListAverageColumnsSwappedSelector = createSelector(
  [dataKiSyokenKiListAverageSortedSelector, kiSyokenColumnsOrderSelector],
  (sortedDataKiSyokenKiListAverage, columnsOrder) => {
    if (sortedDataKiSyokenKiListAverage == null) {
      return sortedDataKiSyokenKiListAverage;
    }

    const swappedFieldsOrder = dataKiSyokenSwapColumnsOrder(
      sortedDataKiSyokenKiListAverage,
      columnsOrder
    );

    return swappedFieldsOrder;
  }
);

/**
 * [機種別商圏] KiListAverageのフィルターやスワップ、列非表示によって整形されたデータ
 */
export const dataKiSyokenKiListAverageResultSelector = createSelector(
  [
    dataKiSyokenKiListAverageColumnsSwappedSelector,
    kiSyokenMksHallCodesSelector,
    kiSyokenSelectedTableFilterSelector,
  ],
  (swappedDataKiSyokenKiListAverage, mkshallsFilter, fieldsFilter) => {
    if (swappedDataKiSyokenKiListAverage == null) {
      return swappedDataKiSyokenKiListAverage;
    }

    const hiddenByMksHall = dataKiSyokenHiddenByMksHall(
      swappedDataKiSyokenKiListAverage,
      mkshallsFilter
    );

    const hiddenByField = dataKiSyokenHiddenByField(
      hiddenByMksHall,
      mkshallsFilter,
      fieldsFilter
    );

    return hiddenByField;
  }
);

/**
 * [機種別商圏] フィルターやスワップ、列非表示によって整形されたデータ
 */
export const dataKiSyokenResultSelector = createSelector(
  [
    dataColumnsSwappedSelector,
    kiSyokenMksHallCodesSelector,
    kiSyokenSelectedTableFilterSelector,
    kiSyokenTagListSelector,
  ],
  (swappedDataKiSyoken, mkshallsFilter, fieldsFilter, kiTagList) => {
    if (swappedDataKiSyoken == null) {
      return swappedDataKiSyoken;
    }

    const hiddenByMksHall = dataKiSyokenHiddenByMksHall(
      swappedDataKiSyoken,
      mkshallsFilter
    );

    const hiddenByField = dataKiSyokenHiddenByField(
      hiddenByMksHall,
      mkshallsFilter,
      fieldsFilter
    );

    return hiddenByField;
  }
);

/**
 * [機種別商圏] KiFilteredTagsAverageのカラムスワップ処理
 */
export const dataKiSyokenKiFilteredTagsAverageColumnsSwappedSelector =
  createSelector(
    [
      dataKiSyokenKiFilteredTagsAverageRemovedSelector,
      kiSyokenColumnsOrderSelector,
    ],
    (removedDataKiSyokenKiFilteredTagsAverage, columnsOrder) => {
      if (removedDataKiSyokenKiFilteredTagsAverage == null) {
        return removedDataKiSyokenKiFilteredTagsAverage;
      }

      const swappedDataKiSyokenKiFilteredTagsAverage =
        dataKiSyokenSwapColumnsOrder(
          removedDataKiSyokenKiFilteredTagsAverage,
          columnsOrder
        );

      return swappedDataKiSyokenKiFilteredTagsAverage;
    }
  );

/**
 * [機種別商圏] KiFilteredTagsAverageのフィルターやスワップ、列非表示によって整形されたデータ
 */
export const dataKiSyokenKiFilteredTagsAverageResultSelector = createSelector(
  [
    dataKiSyokenKiFilteredTagsAverageColumnsSwappedSelector,
    kiSyokenMksHallCodesSelector,
    kiSyokenSelectedTableFilterSelector,
  ],
  (swappedDataKiSyokenKiFilteredTagsAverage, mkshallsFilter, fieldsFilter) => {
    if (swappedDataKiSyokenKiFilteredTagsAverage == null) {
      return swappedDataKiSyokenKiFilteredTagsAverage;
    }

    const hiddenByMksHall = dataKiSyokenHiddenByMksHall(
      swappedDataKiSyokenKiFilteredTagsAverage,
      mkshallsFilter
    );

    const hiddenByField = dataKiSyokenHiddenByField(
      hiddenByMksHall,
      mkshallsFilter,
      fieldsFilter
    );

    return hiddenByField;
  }
);

/**
 * [機種別商圏] KiTagsAverageのカラムスワップ処理
 */
export const dataKiSyokenKiTagsAverageColumnsSwappedSelector = createSelector(
  [dataKiSyokenKiTagsAverageRemovedSelector, kiSyokenColumnsOrderSelector],
  (removedDataKiSyokenKiTagsAverage, columnsOrder) => {
    if (removedDataKiSyokenKiTagsAverage == null) {
      return removedDataKiSyokenKiTagsAverage;
    }

    // 配列の各要素に対してカラムスワップ処理を適用
    return removedDataKiSyokenKiTagsAverage.map((dataItem) => {
      return dataKiSyokenSwapColumnsOrder(dataItem, columnsOrder);
    });
  }
);

/**
 * [機種別商圏] KiTagsAverageのフィルターやスワップ、列非表示によって整形されたデータ
 */
export const dataKiSyokenKiTagsAverageResultSelector = createSelector(
  [
    dataKiSyokenKiTagsAverageColumnsSwappedSelector,
    kiSyokenMksHallCodesSelector,
    kiSyokenSelectedTableFilterSelector,
  ],
  (swappedDataKiSyokenKiTagsAverage, mkshallsFilter, fieldsFilter) => {
    if (swappedDataKiSyokenKiTagsAverage == null) {
      return swappedDataKiSyokenKiTagsAverage;
    }

    // 配列の各要素に対してフィルター処理を適用
    return swappedDataKiSyokenKiTagsAverage.map((dataItem) => {
      const hiddenByMksHall = dataKiSyokenHiddenByMksHall(
        dataItem,
        mkshallsFilter
      );

      const hiddenByField = dataKiSyokenHiddenByField(
        hiddenByMksHall,
        mkshallsFilter,
        fieldsFilter
      );

      return hiddenByField;
    });
  }
);
